import React, { useState, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Row } from 'react-styled-flexboxgrid';
import { IoIosPaper, IoIosHeart, IoIosCall, IoMdMail } from 'react-icons/io';
import { FaBell } from 'react-icons/fa';
import { ProfileAvatarLoader } from '../../components/Placeholder';
import { useQuery } from '@apollo/client';
import { GET_PROFILE_POST } from '@core/ui/graphql/Profile.query';
import UserInformation from '../../components/UserInfoBox';
import TabButtons from '../../components/TabView';
import { useAuth } from '../../contexts/AuthProvider';
import UserListing from './userListing';
import FavouriteListing from './userFavourite';
import { UserContext, useUser } from '../../contexts/UserContext';
import ProfileSection from './style';
import { TabContent } from './style';
import Alerts from '../../components/Alerts';
import { COUNT_ALERTS } from '@core/ui/graphql/Alerts';
import { useTranslation } from 'next-i18next';

const Profile = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const { isLoggedIn } = useAuth();
  const { state: { userId } = {} } = useUser();

  const {
    query: { tabs },
  } = router;

  const username = userId;
  const routerMapping = {
    alerts: 'alerts',
    favorites: 'favorites',
    myClassifieds: 'myClassifieds',
  };
  const [tabStates, setTabState] = useState({
    tabState: routerMapping.hasOwnProperty(tabs)
      ? routerMapping[tabs]
      : 'myClassifieds',
  });
  const [alertsCounter, setAlertsCounter] = useState(0);
  const [publishedPostCount, setPublishedPostCount] = useState(0);

  useEffect(() => {
    const tabState = routerMapping.hasOwnProperty(tabs)
      ? routerMapping[tabs]
      : 'myClassifieds';
    setTabState({ tabState });
  }, [tabs]);

  const handleTabToggle = tabState => async () => {
    const tabStateUrl = routerMapping.hasOwnProperty(tabState)
      ? '/profile/' + routerMapping[tabState]
      : '/profile/myClassifieds';
    await router.push(tabStateUrl, tabStateUrl, { shallow: true });
  };

  const { data: dataAlerts } = useQuery(COUNT_ALERTS, {
    variables: { uid: userId },
    ssr: false,
  });

  const { countAlerts } = dataAlerts || {};

  const QUERY_VARIABLES = {
    USERNAME: username,
    limit: 8,
  };

  const { data, loading, refetch } = useQuery(GET_PROFILE_POST, {
    variables: QUERY_VARIABLES,
    fetchPolicy: 'cache-and-network',
    skip: !username,
  });

  const { state: userState = {} } = useContext(UserContext);

  const {
    avatar: userImage = null,
    favorites = [],
    mail: authorEmail = '',
    mobile: authorPhoneNumber,
    userId: authorId = false,
    userName,
  } = userState;

  const { author: { posts = {} } = {} } = data || {};

  let { total } = posts || {};

  const decrementAdsCount = async () => {
    await refetch();
  };

  useEffect(() => {
    if (!loading) {
      setPublishedPostCount(total);
    }
  }, [loading]);

  useEffect(() => {
    if (countAlerts && countAlerts.total) {
      setAlertsCounter(countAlerts.total);
    }
  }, [dataAlerts]);

  const favouritePostCount = favorites.length;

  return (
    <ProfileSection>
      <div className="user-top">
        {authorEmail !== '' ? (
          <UserInformation
            source={[userImage]}
            imgWidth={120}
            imgHeight={120}
            imgRadius={85}
            informationStyle={{
              marginLeft: 30,
              width: 'auto',
            }}
            author={authorId}
            authorId={authorId}
            userId={userId}
            title={userName}
            titleStyle={{
              display: 'block',
              color: '#333333',
              fontSize: 24,
              fontWeight: 'bold',
              marginBottom: '10px',
              lineHeight: '22px',
              textTransform: 'capitalize',
            }}
            phoneIcon={<IoIosCall size={20} />}
            mailIcon={<IoMdMail style={{ marginTop: '2px' }} size={20} />}
            phoneNumber={authorPhoneNumber}
            authorMail={authorEmail}
          />
        ) : (
          <div className="avatar">
            <ProfileAvatarLoader />
          </div>
        )}
      </div>

      {/* Profile page tab */}
      <div className="tabs-section">
        <div className="tabs">
          <TabButtons
            menuIcon={
              <span
                style={{
                  color:
                    tabStates.tabState === 'myClassifieds'
                      ? '#333333'
                      : '#002F3F',
                  marginRight: 7,
                }}
              >
                <IoIosPaper style={{ color: '#00BCD4' }} size={18} />
              </span>
            }
            className={tabStates.tabState === 'myClassifieds' ? 'active' : ''}
            iconPosition="left"
            menuText={t('myAds')}
            menuTextColor={
              tabStates.tabState === 'myClassifieds' ? '#002F3F' : '#333333'
            }
            count={publishedPostCount}
            onClick={handleTabToggle('myClassifieds')}
          />
          <TabButtons
            menuIcon={
              <span
                style={{
                  color:
                    tabStates.tabState === 'favorites' ? '#333333' : '#002F3F',
                  marginRight: 7,
                }}
              >
                <IoIosHeart style={{ color: '#00BCD4' }} size={18} />
              </span>
            }
            className={tabStates.tabState === 'favorites' ? 'active' : ''}
            iconPosition="left"
            menuText={t('myFavorites')}
            menuTextColor={
              tabStates.tabState === 'favorites' ? '#002F3F' : '#333333'
            }
            count={favouritePostCount}
            onClick={handleTabToggle('favorites')}
          />
          <TabButtons
            menuIcon={
              <span
                style={{
                  color:
                    tabStates.tabState === 'alerts' ? '#333333' : '#002F3F',
                  marginRight: 7,
                }}
              >
                <FaBell style={{ color: '#00BCD4' }} size={18} />
              </span>
            }
            iconPosition="left"
            className={tabStates.tabState === 'alerts' ? 'active' : ''}
            menuText={t('header.alerts.label')}
            menuTextColor={
              tabStates.tabState === 'alerts' ? '#002F3F' : '#333333'
            }
            count={alertsCounter}
            onClick={handleTabToggle('alerts')}
          />
        </div>
      </div>

      <TabContent>
        <Row>
          {/* Tab content */}
          {tabStates.tabState === 'myClassifieds' ? (
            <div className="tab-content">
              <UserListing
                isLoggedIn={isLoggedIn}
                userId={userId}
                decrementAdsCount={decrementAdsCount}
              />
            </div>
          ) : null}
          {tabStates.tabState === 'favorites' ? (
            <div className="tab-content">
              <FavouriteListing
                userId={authorId}
                isLoggedIn={isLoggedIn}
                favorites={favorites}
              />
            </div>
          ) : null}
          {tabStates.tabState === 'alerts' && (
            <div className="tab-content">
              <Alerts
                setCounter={total => setAlertsCounter(total)}
                uid={userId}
              />
            </div>
          )}
        </Row>
      </TabContent>
    </ProfileSection>
  );
};

export default Profile;
